import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ExitDetailsFormValues } from '../index';
import { PreviousExitDetails } from '../types';
import { buildFormResetValues } from '../helpers/build-form-reset-values';

/**
 * Hook to manage form restoration from previousExitDetails
 * Handles both initial setup and restoration when navigating back
 */
export const useFormRestoration = (
  form: UseFormReturn<ExitDetailsFormValues>,
  isEditMode: boolean,
  previousExitDetails: PreviousExitDetails | undefined,
  isBeingCorrected: boolean,
  exitDetailsStepCompleted: boolean,
  usersFirmId?: string,
  initialCorrectionValuesRef?: React.MutableRefObject<ExitDetailsFormValues | null>,
) => {
  // Set values from previousExitDetails in edit mode
  useEffect(() => {
    // Reset the initial correction values ref when previousExitDetails changes
    if (isBeingCorrected && previousExitDetails && initialCorrectionValuesRef) {
      initialCorrectionValuesRef.current = null;
    }

    if (isEditMode && previousExitDetails) {
      const resetValues = buildFormResetValues(
        previousExitDetails,
        isEditMode,
        usersFirmId,
      );
      form.reset(resetValues);

      // Store initial values for corrections to detect changes
      if (
        isBeingCorrected &&
        initialCorrectionValuesRef &&
        !initialCorrectionValuesRef.current
      ) {
        initialCorrectionValuesRef.current = {
          ...resetValues,
          selectedCompany: {
            ...resetValues.selectedCompany,
            _id: resetValues.selectedCompany._id || '',
          },
        };
      }
    }
  }, [
    isEditMode,
    previousExitDetails,
    form,
    isBeingCorrected,
    usersFirmId,
    initialCorrectionValuesRef,
  ]);

  // Restore form values when going back (exitDetailsStepCompleted becomes false)
  useEffect(() => {
    if (!exitDetailsStepCompleted && !isEditMode) {
      if (previousExitDetails) {
        // Restore from previousExitDetails instead of resetting
        const resetValues = buildFormResetValues(
          previousExitDetails,
          true, // treat as edit mode for restoration
          usersFirmId,
        );
        form.reset(resetValues);
      } else {
        // Only reset if there's no previousExitDetails to restore from
        form.reset({
          firmId: usersFirmId || '',
          exitDate: new Date(),
          selectedCompany: undefined,
          selectedShareClasses: [],
          selectedAllocations: [],
          sharesToBeSold: 0,
          sharePrice: '0',
          chargeAccruedFees: undefined,
          taxReliefElegible: undefined,
          taxReliefElegibleAllocationIds: [],
          taxableValueSharePrice: '0',
          isReceivedValueSharePriceEdited: false,
          considerationType: undefined,
          shareConsiderationCompanyId: undefined,
          shareConsiderationShareClassId: undefined,
          totalConsiderationShares: '0',
          shareConsiderationValuePerShare: '0',
          shareConsiderationValueTotal: '0',
          shareConsiderationEisEligible: false,
          shareConsiderationAllocationMethod: undefined,
          permitPartialShareAllocation: false,
        });
      }
    }
  }, [
    exitDetailsStepCompleted,
    isEditMode,
    previousExitDetails,
    form,
    usersFirmId,
  ]);
};
